import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { BACKEND_URL, assetUrl, errMsg, formatBytes, uploadAsset } from '@/lib/digitalApi';
import { SectionPicker, ArticleFields } from '@/admin/digital/ResourceFields';
import { PHOTOS } from '@/constants/content';

const inputCls = 'w-full px-3 py-2 border border-[#eae4d8] rounded-lg text-sm bg-white focus:border-[#C8A15A] focus:outline-none';
const Label = ({ children }) => <div className="text-[10px] tracking-[0.2em] uppercase text-[#888] mb-1.5">{children}</div>;

const UploadBox = ({ label, accept, current, onFile, progress, testid }) => (
  <div>
    <Label>{label}</Label>
    <label className="block border border-dashed border-[#d9cfbd] rounded-xl p-4 bg-white cursor-pointer hover:border-[#C8A15A] transition-colors">
      <input type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} data-testid={testid} />
      <div className="text-sm text-[#111]">{current || 'Choose a file from your computer'}</div>
      <div className="text-xs text-[#888] mt-1">{current ? 'Click to replace' : 'Click to upload'}</div>
      {progress != null && (
        <div className="mt-3 h-[3px] bg-[#f1ece3] rounded-full"><div className="h-full bg-[#C8A15A] rounded-full transition-[width]" style={{ width: `${progress}%` }} /></div>
      )}
    </label>
  </div>
);

const CoverPreview = ({ p, isArticle, onRemove }) => {
  const src = p.cover_url ? assetUrl(p.cover_url) : (isArticle ? PHOTOS.editorial : null);
  if (!src) return <p className="mt-3 text-xs text-[#888]" data-testid="dp-editor-cover-none">No cover yet. A neutral placeholder shows on the site.</p>;
  return (
    <div className="mt-3 flex items-start gap-4" data-testid="dp-editor-cover-block">
      <img src={src} alt="" className={`w-28 h-36 object-cover rounded-xl border border-[#eae4d8] ${p.cover_url ? '' : 'opacity-70'}`} data-testid="dp-editor-cover-preview" />
      <div className="text-xs leading-relaxed">
        {p.cover_url ? (
          <>
            <div className="text-[#111]" data-testid="dp-editor-cover-status">Custom cover in use</div>
            <button type="button" onClick={onRemove} className="mt-2 text-red-700 underline" data-testid="dp-editor-remove-cover">Remove image</button>
          </>
        ) : (
          <div className="inline-block px-2 py-1 rounded-full bg-[#f1ece3] text-[#8a7a5c] uppercase tracking-[0.14em] text-[10px]" data-testid="dp-editor-cover-status">Using fallback photo</div>
        )}
      </div>
    </div>
  );
};

export default function ProductEditor({ id, meta, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});

  const load = async (mode = 'reset') => {
    const r = await adminApi.get(`/admin/digital-products/${id}`);
    setP(r.data);
    if (mode === 'keep') return;
    const next = {
      title: r.data.title || '', slug: r.data.slug || '', short_description: r.data.short_description || '',
      description: r.data.description || '', format: r.data.format || '', category_id: r.data.category_id || '',
      is_free: r.data.is_free, price_usd: r.data.price_usd || '', sort_order: r.data.sort_order || 0,
      sections: r.data.sections || ['digital-products'], content_type: r.data.content_type || 'download',
      body_html: r.data.body_html || '', published_date: r.data.published_date || '', read_time: r.data.read_time_custom || '',
      kicker: r.data.kicker || '', unlisted: !!r.data.unlisted, primary_cta: r.data.primary_cta || null, secondary_cta: r.data.secondary_cta || null,
    };
    setForm((prev) => (mode === 'init' && prev ? prev : next));
  };
  useEffect(() => { load('init').catch(() => toast.error('Failed to load product')); /* eslint-disable-next-line */ }, [id]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const needsPrice = form.content_type !== 'article' || p?.file;
    if (needsPrice && !form.is_free && !String(form.price_usd).trim()) { toast.error('Enter a USD price for paid items.'); return false; }
    if (!form.sections.length) { toast.error('Choose at least one section.'); return false; }
    setBusy(true);
    try {
      const body = { ...form, format: form.format || null, category_id: form.category_id || null, sort_order: Number(form.sort_order) || 0 };
      if (form.is_free || !String(form.price_usd).trim()) delete body.price_usd;
      await adminApi.patch(`/admin/digital-products/${id}`, body);
      await load(); onSaved(); toast.success('Saved');
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };

  const setStatus = async (action) => {
    if (action === 'publish' && !(await save())) return;
    try { await adminApi.post(`/admin/digital-products/${id}/${action}`); await load('keep'); onSaved(); toast.success(action === 'publish' ? 'Published' : 'Unpublished'); } catch (e) { toast.error(errMsg(e)); }
  };

  const removeCover = async () => {
    if (!window.confirm(form.content_type === 'article' ? 'Remove this cover? The article will use the fallback photograph.' : 'Remove this cover image?')) return;
    try { await adminApi.post(`/admin/digital-products/${id}/remove-cover`); await load('keep'); onSaved(); toast.success('Cover removed'); } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async (kind, file) => {
    const max = kind === 'cover' ? meta.limits.max_cover_bytes : meta.limits.max_file_bytes;
    if (file.size > max) { toast.error(`File is larger than ${formatBytes(max)}.`); return; }
    setProgress((s) => ({ ...s, [kind]: 0 }));
    try {
      await uploadAsset(id, kind, file, (v) => setProgress((s) => ({ ...s, [kind]: v })));
      toast.success(kind === 'cover' ? 'Cover uploaded' : 'Product file uploaded');
      await load('keep'); onSaved();
    } catch (e) { toast.error(errMsg(e, 'Upload failed. Please retry.')); }
    setProgress((s) => ({ ...s, [kind]: null }));
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="dp-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#FAF8F5] h-full overflow-y-auto shadow-2xl" data-lenis-prevent>
        <div className="sticky top-0 z-10 bg-[#FAF8F5] border-b border-[#eae4d8] px-6 py-4 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">Resource · <span data-testid="dp-editor-status">{p?.status}</span></div>
          <button onClick={onClose} className="text-sm text-[#555] hover:text-[#111]" data-testid="dp-editor-close">Close ×</button>
        </div>
        {!form ? <div className="px-6 py-16 text-center text-[#888]">Loading…</div> : (
          <div className="px-6 py-6 space-y-6">
            <div><Label>Title</Label><input value={form.title} onChange={set('title')} className={inputCls} data-testid="dp-editor-title" /></div>
            <div><Label>URL slug</Label><input value={form.slug} onChange={set('slug')} className={inputCls} data-testid="dp-editor-slug" />
              <div className="text-xs text-[#888] mt-1">/resources/digital-products/{form.slug}</div></div>
            <SectionPicker form={form} setForm={setForm} />
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Format{form.content_type === 'article' ? ' (optional)' : ''}</Label>
                <select value={form.format} onChange={set('format')} className={inputCls} data-testid="dp-editor-format">
                  <option value="">Select format</option>{meta.formats.map((f) => <option key={f} value={f}>{f}</option>)}
                </select></div>
              <div><Label>Category</Label>
                <select value={form.category_id} onChange={set('category_id')} className={inputCls} data-testid="dp-editor-category">
                  <option value="">No category</option>{meta.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            </div>
            <div>
              <Label>{form.content_type === 'article' ? 'Attachment pricing (only if you upload an attachment)' : 'Pricing'}</Label>
              <div className="flex gap-2">
                {[['Free', true], ['Paid', false]].map(([l, v]) => (
                  <button key={l} type="button" onClick={() => setForm((f) => ({ ...f, is_free: v }))} data-testid={`dp-editor-pricing-${l.toLowerCase()}`}
                    className={`text-xs tracking-[0.14em] uppercase px-5 py-2 rounded-full border ${form.is_free === v ? 'bg-[#111] text-white border-[#111]' : 'bg-white border-[#eae4d8] text-[#555]'}`}>{l}</button>
                ))}
              </div>
              {!form.is_free && (
                <div className="mt-3 flex items-center gap-2 max-w-[220px]">
                  <span className="text-[#555]">$</span>
                  <input required inputMode="decimal" value={form.price_usd} onChange={set('price_usd')} placeholder="19.99" className={inputCls} data-testid="dp-editor-price" />
                  <span className="text-xs text-[#888]">USD</span>
                </div>
              )}
              <p className="text-xs text-[#888] mt-2">{form.is_free ? 'Visitors enter name and email, then download instantly. No Stripe checkout.' : 'Stripe checkout uses this saved price automatically. Changes apply to new purchases only.'}</p>
            </div>
            <div><Label>{form.content_type === 'article' ? 'Summary / dek (cards, subtitle & search description)' : 'Short description (cards & subtitle)'}</Label><textarea rows={2} maxLength={400} value={form.short_description} onChange={set('short_description')} className={inputCls} data-testid="dp-editor-short-description" /></div>
            {form.content_type === 'article'
              ? <ArticleFields form={form} setForm={setForm} />
              : <div><Label>Full description (blank line = new paragraph)</Label><textarea rows={8} value={form.description} onChange={set('description')} className={inputCls} data-testid="dp-editor-description" /></div>}
            <div><Label>Display order (lower shows first)</Label><input type="number" value={form.sort_order} onChange={set('sort_order')} className={`${inputCls} max-w-[120px]`} data-testid="dp-editor-sort-order" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <UploadBox label={form.content_type === 'article' ? 'Attachment (optional, private, ≤ 250 MB)' : 'Product file (private, ≤ 250 MB)'} accept={meta.limits.file_exts.map((e) => `.${e}`).join(',')}
                current={p.file && `${p.file.filename} · ${formatBytes(p.file.size)}`} progress={progress.file} onFile={(f) => upload('file', f)} testid="dp-editor-file-input" />
              <div>
                <UploadBox label="Cover image (JPG/PNG/WebP, ≤ 10 MB)" accept="image/jpeg,image/png,image/webp"
                  current={p.has_cover && 'Cover uploaded'} optional={form.content_type === 'article'} progress={progress.cover} onFile={(f) => upload('cover', f)} testid="dp-editor-cover-input" />
                <CoverPreview p={p} isArticle={form.content_type === 'article'} onRemove={removeCover} />
              </div>
            </div>
            {p.file && (
              <a href={`${BACKEND_URL}/api/admin/digital-products/${id}/file`} className="inline-block text-xs underline text-[#555]" data-testid="dp-editor-download-file">Download current file (admin)</a>
            )}
            {p.file_history_count > 0 && <p className="text-xs text-[#888]">{p.file_history_count} previous file version(s) retired. Past buyers now receive the latest file.</p>}
            <div className="sticky bottom-0 bg-[#FAF8F5] border-t border-[#eae4d8] -mx-6 px-6 py-4 flex flex-wrap gap-3">
              <button type="button" disabled={busy} onClick={save} className="text-xs tracking-[0.18em] uppercase px-5 py-2.5 border border-[#111] rounded-full disabled:opacity-50" data-testid="dp-editor-save">Save Draft</button>
              {p.status !== 'published'
                ? <button type="button" disabled={busy} onClick={() => setStatus('publish')} className="text-xs tracking-[0.18em] uppercase px-5 py-2.5 bg-[#C8A15A] text-[#111] rounded-full disabled:opacity-50" data-testid="dp-editor-publish">Save &amp; Publish</button>
                : <button type="button" onClick={() => setStatus('unpublish')} className="text-xs tracking-[0.18em] uppercase px-5 py-2.5 border border-amber-700 text-amber-800 rounded-full" data-testid="dp-editor-unpublish">Unpublish</button>}
              {p.status === 'published' && <a href={form.content_type === 'article' && p.sections?.[0] === 'journal' ? `/journal/${p.slug}` : `/resources/${p.sections?.[0] || 'digital-products'}/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs tracking-[0.18em] uppercase px-5 py-2.5 text-[#555] underline" data-testid="dp-editor-view-live">View Page</a>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
